import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stack, useDisclosure } from '@chakra-ui/react';
import {
  faArrowRightArrowLeft,
  faArrowsRotate,
  faUserCheck,
  faUserEdit,
  faUserSlash,
  faUserXmark,
} from '@fortawesome/pro-regular-svg-icons';

import { ConfirmationModal, IConfirmationModalData } from 'shared-components';

import { SidebarGroup } from '../../../molecules/studentActions/Group';
import { SidebarAction } from '../../../molecules/studentActions/Action';
import ChangeStudentNoModal from '../../modals/changeStudentNo';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteStudent,
  makeActive,
  revokeStudentAccess,
  removeImportHash,
  changeStudentNo,
} from '../../../../store/student/actions';
import { getConfig } from '../../../../store/selectors';
import { hasPerm, P } from '../../../../utilities/roles/permissions';
import { UserRole } from '../../../../store/auth/types';
import { ImmigrationProvider } from './immigration-provider';
import {
  triggerSITSTransfer,
  studentsRemoveImmigrationProvider,
  studentsAddImmigrationProvider,
} from '../../../../store/dashboard/actions';
import { useTranslation } from "react-i18next";

interface IProps {
  userRole: UserRole;
  studentId: string;
  currentImmigrationProvider: string | null;
  immigrationProviders: string[];
}

export const AdminActions = (props: IProps) => {
    const { t } = useTranslation('tables');
  const config = useSelector(getConfig);
  const navigate = useNavigate();
  const { userRole, studentId, currentImmigrationProvider, immigrationProviders } = props;

  const canViewAdminActionDropdown = hasPerm(userRole, P.VIEW_ADMIN_ACTION_DROPDOWN);
  const isSITSAvailable = !!config.sits;

  const dispatch = useDispatch();

  const [, studentNo] = studentId.split('#');

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [confirmationModalData, setConfirmationModalData] = useState<IConfirmationModalData>({
    clickAction: () => {},
    title: '',
    buttonName: '',
  });

  const [isChangeStudentNoModal, setChangeStudentNoModal] = useState(false);

  const onTriggerSITSTransfer = () => dispatch(triggerSITSTransfer(studentNo));

  const onDeleteImmigrationProvider = () => dispatch(studentsRemoveImmigrationProvider([studentId]));

  const onChangeImmigrationProvider = ({ provider }: { provider: string }) =>
    dispatch(studentsAddImmigrationProvider([studentId], provider));

  const onDelete = () => {
    setConfirmationModalData({
      clickAction: () => {
        dispatch(deleteStudent());
        // navigateToUrl(`${baseUrl}`);
        // window.location.href = baseUrl;
        navigate('/');
      },
      title: `Are you sure?`,
      isDestructive: true,
      buttonName: 'Delete',
    });
    onOpen();
  };

  const onMakeActive = () => {
    setConfirmationModalData({
      clickAction: () => {
        dispatch(makeActive());
      },
      title: `Are you sure?`,
      isDestructive: false,
      buttonName: 'Make Active',
    });
    onOpen();
  };

  const onRevokeStudentAccess = () => {
    setConfirmationModalData({
      clickAction: () => {
        dispatch(revokeStudentAccess());
      },
      title: `Are you sure?`,
      isDestructive: true,
      buttonName: 'Revoke Access',
    });
    onOpen();
  };

  const onChangeStudentNumber = () => {
    setChangeStudentNoModal(true);
  };

  const onRemoveImportHash = () => {
    setConfirmationModalData({
      clickAction: () => {
        dispatch(removeImportHash());
      },
      title: `Are you sure?`,
      isDestructive: false,
      buttonName: 'Force update',
    });
    onOpen();
  };

  return (
    <>
      {canViewAdminActionDropdown && (
        <SidebarGroup header="Admin actions">
          <Stack spacing={0} mx={-3}>
            <SidebarAction onClick={onDelete} icon={faUserXmark}>
              {t('tables:deleteStudent')}
                                      </SidebarAction>

            <SidebarAction onClick={onMakeActive} icon={faUserCheck}>
              {t('tables:makeActiveRecord')}
                                      </SidebarAction>

            <SidebarAction onClick={onChangeStudentNumber} icon={faUserEdit}>
              {t('tables:changeStudentNumber')}
                                      </SidebarAction>

            <SidebarAction onClick={onRevokeStudentAccess} icon={faUserSlash}>
              {t('tables:revokeStudentAccess')}
                                      </SidebarAction>

            <SidebarAction onClick={onRemoveImportHash} icon={faArrowsRotate}>
              {t('tables:forceUpdateOnNextSync')}
                                      </SidebarAction>

            {isSITSAvailable && (
              <SidebarAction onClick={onTriggerSITSTransfer} icon={faArrowRightArrowLeft}>
                {t('tables:triggerSitsTransfer')}
                                            </SidebarAction>
            )}

            <ImmigrationProvider
              immigrationProviders={immigrationProviders}
              selectedValue={currentImmigrationProvider}
              onDelete={() => onDeleteImmigrationProvider()}
              onChange={(provider: string) => onChangeImmigrationProvider({ provider })}
            />
          </Stack>
        </SidebarGroup>
      )}

      <ConfirmationModal
        isOpen={isOpen}
        titleText={confirmationModalData.title}
        descriptionText={confirmationModalData.descriptionText}
        confirmationButtonText={confirmationModalData.buttonName}
        isDestructive={confirmationModalData.isDestructive}
        onClickSuccess={() => {
          confirmationModalData.clickAction();
          onClose();
        }}
        onClose={onClose}
      />

      {isChangeStudentNoModal && (
        <ChangeStudentNoModal
          value={studentNo}
          onChange={val => {
            dispatch(changeStudentNo(val));
            setChangeStudentNoModal(false);
            navigate('/');
          }}
          onClose={() => setChangeStudentNoModal(false)}
        />
      )}
    </>
  );
};
